import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';

import { auditLogAdapter } from '../shared-audit.adapters';
import * as actions from './trip.actions';
import {
  FlightAudit,
  FlightBusinessPersonAudit,
  FlightCrewMemberAudit,
  FlightCrewMemberCateringAudit,
  FlightCrewMemberHotelAudit,
  FlightCrewMemberTransportationAudit,
  FlightPassengerAudit,
  FlightPassengerCateringAudit,
  FlightPassengerHotelAudit,
  FlightPassengerTransportationAudit,
  LegNoteAudit,
  FlightFuelAudit,
  ScheduledAircraftTripAudit,
  ScheduledAircraftTripSkeleton,
  ScheduledEventAudit,
  ScheduledFlightAudit,
  AuditLogLite,
  DeletedTripItem,
  DeletedTripLegItem,
  TripLegItem,
  FlightCrewMemberItem,
  FlightPassengerItem,
  FlightSchedulerItem
} from '../../+models/trip-audit.model';

// States
interface DeletedScheduledAircraftTripSkeletonState
  extends EntityState<ScheduledAircraftTripSkeleton> {
  loaded: boolean;
  loading: boolean;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface ScheduledAircraftTripSkeletonState extends EntityState<ScheduledAircraftTripSkeleton> {
  loaded: boolean;
  loading: boolean;
}

interface ScheduledAircraftTripAuditState extends EntityState<ScheduledAircraftTripAudit> {
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface ScheduledFlightAuditState extends EntityState<ScheduledFlightAudit> {
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface FlightAuditState extends EntityState<FlightAudit> {
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface FlightPassengerAuditState extends EntityState<FlightPassengerAudit> {
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface FlightPassengerCateringAuditState extends EntityState<FlightPassengerCateringAudit> {
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface FlightPassengerHotelAuditState extends EntityState<FlightPassengerHotelAudit> {
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface FlightPassengerTransportationAuditState
  extends EntityState<FlightPassengerTransportationAudit> {
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface FlightCrewMemberAuditState extends EntityState<FlightCrewMemberAudit> {
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface FlightCrewMemberCateringAuditState extends EntityState<FlightCrewMemberCateringAudit> {
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface FlightCrewMemberHotelAuditState extends EntityState<FlightCrewMemberHotelAudit> {
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface FlightCrewMemberTransportationAuditState
  extends EntityState<FlightCrewMemberTransportationAudit> {
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface FlightBusinessPersonAuditState extends EntityState<FlightBusinessPersonAudit> {
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface ScheduledEventAuditState extends EntityState<ScheduledEventAudit> {
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface LegNoteAuditState extends EntityState<LegNoteAudit> {
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface FlightFuelAuditState extends EntityState<FlightFuelAudit> {
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

// V2 States for lazy loading

interface DeletedTripsState extends EntityState<DeletedTripItem> {
  loaded: boolean;
  loading: boolean;
  pageNumber: number;
}

interface TripLogsState extends EntityState<AuditLogLite> {
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastId: number | null;
  totalCount: number;
}

interface DeletedTripLegsState extends EntityState<DeletedTripLegItem> {
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  totalCount: number;
}

interface TripLegsState extends EntityState<TripLegItem> {
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  totalCount: number;
}

interface LegEventLogsState extends EntityState<AuditLogLite> {
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastId: number | null;
  totalCount: number;
}

interface LegFlightLogsState extends EntityState<AuditLogLite> {
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastId: number | null;
  totalCount: number;
}

interface LegFuelLogsState extends EntityState<AuditLogLite> {
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastId: number | null;
  totalCount: number;
}

interface FlightCrewMembersState extends EntityState<FlightCrewMemberItem> {
  loaded: boolean;
  loading: boolean;
  selectedFlightId: number | null;
  pageNumber: number;
  totalCount: number;
}

interface CrewLogsState extends EntityState<AuditLogLite> {
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastId: number | null;
  totalCount: number;
}

interface CrewCateringLogsState extends EntityState<AuditLogLite> {
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastId: number | null;
  totalCount: number;
}

interface CrewHotelLogsState extends EntityState<AuditLogLite> {
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastId: number | null;
  totalCount: number;
}

interface CrewTransportationLogsState extends EntityState<AuditLogLite> {
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastId: number | null;
  totalCount: number;
}

interface FlightPassengersState extends EntityState<FlightPassengerItem> {
  loaded: boolean;
  loading: boolean;
  selectedFlightId: number | null;
  pageNumber: number;
  totalCount: number;
}

interface PassengerLogsState extends EntityState<AuditLogLite> {
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastId: number | null;
  totalCount: number;
}

interface PassengerCateringLogsState extends EntityState<AuditLogLite> {
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastId: number | null;
  totalCount: number;
}

interface PassengerHotelLogsState extends EntityState<AuditLogLite> {
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastId: number | null;
  totalCount: number;
}

interface PassengerTransportationLogsState extends EntityState<AuditLogLite> {
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastId: number | null;
  totalCount: number;
}

interface FlightSchedulersState extends EntityState<FlightSchedulerItem> {
  loaded: boolean;
  loading: boolean;
  selectedFlightId: number | null;
  pageNumber: number;
  totalCount: number;
}

interface SchedulerLogsState extends EntityState<AuditLogLite> {
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastId: number | null;
  totalCount: number;
}

// Adapters
const deletedScheduledAircraftTripSkeletonsAdapter =
  createEntityAdapter<ScheduledAircraftTripSkeleton>({
    selectId: p => p.scheduledAircraftTripId
  });
const scheduledAircraftTripSkeletonAdapter = createEntityAdapter<ScheduledAircraftTripSkeleton>({
  selectId: p => p.scheduledAircraftTripId
});

// V2 Adapters
const deletedTripsAdapter = createEntityAdapter<DeletedTripItem>({
  selectId: p => p.id
});
const tripLogsAdapter = createEntityAdapter<AuditLogLite>({
  selectId: p => p.id
});
const deletedTripLegsAdapter = createEntityAdapter<DeletedTripLegItem>({
  selectId: p => p.flightId
});
const tripLegsAdapter = createEntityAdapter<TripLegItem>({
  selectId: p => p.flightId
});
const legEventLogsAdapter = createEntityAdapter<AuditLogLite>({
  selectId: p => p.id
});
const legFlightLogsAdapter = createEntityAdapter<AuditLogLite>({
  selectId: p => p.id
});
const legFuelLogsAdapter = createEntityAdapter<AuditLogLite>({
  selectId: p => p.id
});
const crewLogsAdapter = createEntityAdapter<AuditLogLite>({
  selectId: p => p.id
});
const crewCateringLogsAdapter = createEntityAdapter<AuditLogLite>({
  selectId: p => p.id
});
const crewHotelLogsAdapter = createEntityAdapter<AuditLogLite>({
  selectId: p => p.id
});
const crewTransportationLogsAdapter = createEntityAdapter<AuditLogLite>({
  selectId: p => p.id
});
const flightCrewMembersAdapter = createEntityAdapter<FlightCrewMemberItem>({
  selectId: p => p.crewId
});
const flightPassengersAdapter = createEntityAdapter<FlightPassengerItem>({
  selectId: p => p.paxId
});
const passengerLogsAdapter = createEntityAdapter<AuditLogLite>({
  selectId: p => p.id
});
const passengerCateringLogsAdapter = createEntityAdapter<AuditLogLite>({
  selectId: p => p.id
});
const passengerHotelLogsAdapter = createEntityAdapter<AuditLogLite>({
  selectId: p => p.id
});
const passengerTransportationLogsAdapter = createEntityAdapter<AuditLogLite>({
  selectId: p => p.id
});
const flightSchedulersAdapter = createEntityAdapter<FlightSchedulerItem>({
  selectId: s => s.schedulerId
});
const schedulerLogsAdapter = createEntityAdapter<AuditLogLite>({
  selectId: l => l.id
});

// Entity Selectors
export const deletedScheduledAircraftTripSkeletonSelectors =
  deletedScheduledAircraftTripSkeletonsAdapter.getSelectors();
export const scheduledAircraftTripSkeletonSelectors =
  scheduledAircraftTripSkeletonAdapter.getSelectors();
export const scheduledAircraftTripAuditSelectors = auditLogAdapter.getSelectors();
export const scheduledFlightAuditSelectors = auditLogAdapter.getSelectors();
export const flightAuditSelectors = auditLogAdapter.getSelectors();
export const flightPassengerAuditSelectors = auditLogAdapter.getSelectors();
export const flightPassengerCateringAuditSelectors = auditLogAdapter.getSelectors();
export const flightPassengerHotelAuditSelectors = auditLogAdapter.getSelectors();
export const flightPassengerTransportationAuditSelectors = auditLogAdapter.getSelectors();
export const flightCrewMemberAuditSelectors = auditLogAdapter.getSelectors();
export const flightCrewMemberCateringAuditSelectors = auditLogAdapter.getSelectors();
export const flightCrewMemberHotelAuditSelectors = auditLogAdapter.getSelectors();
export const flightCrewMemberTransportationAuditSelectors = auditLogAdapter.getSelectors();
export const flightBusinessPersonAuditSelectors = auditLogAdapter.getSelectors();
export const cateringSegmentAuditSelectors = auditLogAdapter.getSelectors();
export const scheduledEventAuditSelectors = auditLogAdapter.getSelectors();
export const legNoteAuditSelectors = auditLogAdapter.getSelectors();
export const flightFuelAuditSelectors = auditLogAdapter.getSelectors();
export const deletedTripsSelectors = deletedTripsAdapter.getSelectors();
export const tripLogsSelectors = tripLogsAdapter.getSelectors();
export const deletedTripLegsSelectors = deletedTripLegsAdapter.getSelectors();
export const tripLegsSelectors = tripLegsAdapter.getSelectors();
export const legEventLogsSelectors = legEventLogsAdapter.getSelectors();
export const legFlightLogsSelectors = legFlightLogsAdapter.getSelectors();
export const legFuelLogsSelectors = legFuelLogsAdapter.getSelectors();
export const crewLogsSelectors = crewLogsAdapter.getSelectors();
export const crewCateringLogsSelectors = crewCateringLogsAdapter.getSelectors();
export const crewHotelLogsSelectors = crewHotelLogsAdapter.getSelectors();
export const crewTransportationLogsSelectors = crewTransportationLogsAdapter.getSelectors();
export const flightCrewMembersSelectors = flightCrewMembersAdapter.getSelectors();
export const flightPassengersSelectors = flightPassengersAdapter.getSelectors();
export const passengerLogsSelectors = passengerLogsAdapter.getSelectors();
export const passengerCateringLogsSelectors = passengerCateringLogsAdapter.getSelectors();
export const passengerHotelLogsSelectors = passengerHotelLogsAdapter.getSelectors();
export const passengerTransportationLogsSelectors = passengerTransportationLogsAdapter.getSelectors();
export const flightSchedulersSelectors = flightSchedulersAdapter.getSelectors();
export const schedulerLogsSelectors = schedulerLogsAdapter.getSelectors();

// Initial states
const deletedScheduledAircraftTripSkeletonsInitialState =
  deletedScheduledAircraftTripSkeletonsAdapter.getInitialState({
    loaded: false,
    loading: false,
    pageNumber: 1,
    lastAuditLogId: null
  });

const scheduledAircraftTripSkeletonInitialState =
  scheduledAircraftTripSkeletonAdapter.getInitialState({
    loaded: false,
    loading: false
  });

const scheduledAircraftTripAuditInitialState = auditLogAdapter.getInitialState({
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});

const scheduledFlightAuditInitialState = auditLogAdapter.getInitialState({
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});

const flightAuditInitialState = auditLogAdapter.getInitialState({
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});

const flightPassengerAuditInitialState = auditLogAdapter.getInitialState({
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});

const flightPassengerCateringAuditInitialState = auditLogAdapter.getInitialState({
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});

const flightPassengerHotelAuditInitialState = auditLogAdapter.getInitialState({
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});

const flightPassengerTransportationAuditInitialState = auditLogAdapter.getInitialState({
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});

const flightCrewMemberAuditInitialState = auditLogAdapter.getInitialState({
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});

const flightCrewMemberCateringAuditInitialState = auditLogAdapter.getInitialState({
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});

const flightCrewMemberHotelAuditInitialState = auditLogAdapter.getInitialState({
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});

const flightCrewMemberTransportationAuditInitialState = auditLogAdapter.getInitialState({
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});

const flightBusinessPersonAuditInitialState = auditLogAdapter.getInitialState({
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});

const scheduledEventAuditInitialState = auditLogAdapter.getInitialState({
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});

const legNoteAuditInitialState = auditLogAdapter.getInitialState({
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});

const flightFuelAuditInitialState = auditLogAdapter.getInitialState({
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});

// V2 Initial states
const deletedTripsInitialState = deletedTripsAdapter.getInitialState({
  loaded: false,
  loading: false,
  pageNumber: 1
});

const tripLogsInitialState = tripLogsAdapter.getInitialState({
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastId: null,
  totalCount: 0
});

const deletedTripLegsInitialState = deletedTripLegsAdapter.getInitialState({
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  totalCount: 0
});

const tripLegsInitialState = tripLegsAdapter.getInitialState({
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  totalCount: 0
});

const legEventLogsInitialState = legEventLogsAdapter.getInitialState({
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastId: null,
  totalCount: 0
});

const legFlightLogsInitialState = legFlightLogsAdapter.getInitialState({
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastId: null,
  totalCount: 0
});

const legFuelLogsInitialState = legFuelLogsAdapter.getInitialState({
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastId: null,
  totalCount: 0
});

const flightCrewMembersInitialState = flightCrewMembersAdapter.getInitialState({
  loaded: false,
  loading: false,
  selectedFlightId: null,
  pageNumber: 1,
  totalCount: 0
});

const crewLogsInitialState = crewLogsAdapter.getInitialState({
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastId: null,
  totalCount: 0
});

const crewCateringLogsInitialState = crewCateringLogsAdapter.getInitialState({
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastId: null,
  totalCount: 0
});

const crewHotelLogsInitialState = crewHotelLogsAdapter.getInitialState({
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastId: null,
  totalCount: 0
});

const crewTransportationLogsInitialState = crewTransportationLogsAdapter.getInitialState({
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastId: null,
  totalCount: 0
});

const flightPassengersInitialState = flightPassengersAdapter.getInitialState({
  loaded: false,
  loading: false,
  selectedFlightId: null,
  pageNumber: 1,
  totalCount: 0
});

const passengerLogsInitialState = passengerLogsAdapter.getInitialState({
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastId: null,
  totalCount: 0
});

const passengerCateringLogsInitialState = passengerCateringLogsAdapter.getInitialState({
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastId: null,
  totalCount: 0
});

const passengerHotelLogsInitialState = passengerHotelLogsAdapter.getInitialState({
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastId: null,
  totalCount: 0
});

const passengerTransportationLogsInitialState = passengerTransportationLogsAdapter.getInitialState({
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastId: null,
  totalCount: 0
});

const flightSchedulersInitialState: FlightSchedulersState = flightSchedulersAdapter.getInitialState({
  loaded: false,
  loading: false,
  selectedFlightId: null,
  pageNumber: 1,
  totalCount: 0
});

const schedulerLogsInitialState: SchedulerLogsState = schedulerLogsAdapter.getInitialState({
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastId: null,
  totalCount: 0
});

export interface State {
  deletedScheduledAircraftTripSkeletons: DeletedScheduledAircraftTripSkeletonState;
  scheduledAircraftTripSkeleton: ScheduledAircraftTripSkeletonState;
  scheduledAircraftTripAudit: ScheduledAircraftTripAuditState;
  scheduledFlightAudit: ScheduledFlightAuditState;
  flightAudit: FlightAuditState;
  flightPassengerAudit: FlightPassengerAuditState;
  flightPassengerCateringAudit: FlightPassengerCateringAuditState;
  flightPassengerHotelAudit: FlightPassengerHotelAuditState;
  flightPassengerTransportationAudit: FlightPassengerTransportationAuditState;
  flightCrewMemberAudit: FlightCrewMemberAuditState;
  flightCrewMemberCateringAudit: FlightCrewMemberCateringAuditState;
  flightCrewMemberHotelAudit: FlightCrewMemberHotelAuditState;
  flightCrewMemberTransportationAudit: FlightCrewMemberTransportationAuditState;
  flightBusinessPersonAudit: FlightBusinessPersonAuditState;
  scheduledEventAudit: ScheduledEventAuditState;
  legNoteAudit: LegNoteAuditState;
  flightFuelAudit: FlightFuelAuditState;
  deletedTrips: DeletedTripsState;
  tripLogs: TripLogsState;
  deletedTripLegs: DeletedTripLegsState;
  tripLegs: TripLegsState;
  legEventLogs: LegEventLogsState;
  legFlightLogs: LegFlightLogsState;
  legFuelLogs: LegFuelLogsState;
  flightCrewMembers: FlightCrewMembersState;
  crewLogs: CrewLogsState;
  crewCateringLogs: CrewCateringLogsState;
  crewHotelLogs: CrewHotelLogsState;
  crewTransportationLogs: CrewTransportationLogsState;
  flightPassengers: FlightPassengersState;
  passengerLogs: PassengerLogsState;
  passengerCateringLogs: PassengerCateringLogsState;
  passengerHotelLogs: PassengerHotelLogsState;
  passengerTransportationLogs: PassengerTransportationLogsState;
  flightSchedulers: FlightSchedulersState;
  schedulerLogs: SchedulerLogsState;
}

const initialState: State = {
  deletedScheduledAircraftTripSkeletons: deletedScheduledAircraftTripSkeletonsInitialState,
  scheduledAircraftTripSkeleton: scheduledAircraftTripSkeletonInitialState,
  scheduledAircraftTripAudit: scheduledAircraftTripAuditInitialState,
  scheduledFlightAudit: scheduledFlightAuditInitialState,
  flightAudit: flightAuditInitialState,
  flightPassengerAudit: flightPassengerAuditInitialState,
  flightPassengerCateringAudit: flightPassengerCateringAuditInitialState,
  flightPassengerHotelAudit: flightPassengerHotelAuditInitialState,
  flightPassengerTransportationAudit: flightPassengerTransportationAuditInitialState,
  flightCrewMemberAudit: flightCrewMemberAuditInitialState,
  flightCrewMemberCateringAudit: flightCrewMemberCateringAuditInitialState,
  flightCrewMemberHotelAudit: flightCrewMemberHotelAuditInitialState,
  flightCrewMemberTransportationAudit: flightCrewMemberTransportationAuditInitialState,
  flightBusinessPersonAudit: flightBusinessPersonAuditInitialState,
  scheduledEventAudit: scheduledEventAuditInitialState,
  legNoteAudit: legNoteAuditInitialState,
  flightFuelAudit: flightFuelAuditInitialState,
  deletedTrips: deletedTripsInitialState,
  tripLogs: tripLogsInitialState,
  deletedTripLegs: deletedTripLegsInitialState,
  tripLegs: tripLegsInitialState,
  legEventLogs: legEventLogsInitialState,
  legFlightLogs: legFlightLogsInitialState,
  legFuelLogs: legFuelLogsInitialState,
  flightCrewMembers: flightCrewMembersInitialState,
  crewLogs: crewLogsInitialState,
  crewCateringLogs: crewCateringLogsInitialState,
  crewHotelLogs: crewHotelLogsInitialState,
  crewTransportationLogs: crewTransportationLogsInitialState,
  flightPassengers: flightPassengersInitialState,
  passengerLogs: passengerLogsInitialState,
  passengerCateringLogs: passengerCateringLogsInitialState,
  passengerHotelLogs: passengerHotelLogsInitialState,
  passengerTransportationLogs: passengerTransportationLogsInitialState,
  flightSchedulers: flightSchedulersInitialState,
  schedulerLogs: schedulerLogsInitialState
};

const tripReducer = createReducer<State>(
  initialState,
  on(actions.ResetDeletedScheduledTrip, () => initialState),

  on(actions.LoadDeletedScheduledAircraftTripSkeletons, state => ({
    ...state,

    deletedScheduledAircraftTripSkeletons: {
      ...state.deletedScheduledAircraftTripSkeletons,
      loading: true,
      loaded: false
    }
  })),

  on(actions.DeletedScheduledAircraftTripSkeletonsLoaded, (state, action) => ({
    ...state,

    deletedScheduledAircraftTripSkeletons: {
      ...state.deletedScheduledAircraftTripSkeletons,
      ...(!state.deletedScheduledAircraftTripSkeletons.lastAuditLogId
        ? deletedScheduledAircraftTripSkeletonsAdapter.setAll(
            action.resultSet,
            state.deletedScheduledAircraftTripSkeletons
          )
        : deletedScheduledAircraftTripSkeletonsAdapter.addMany(
            action.resultSet,
            state.deletedScheduledAircraftTripSkeletons
          )),
      loading: false,
      loaded: true,
      pageNumber: action.pageNumber,
      lastAuditLogId:
        action.resultSet && action.resultSet.length > 0
          ? Math.min(...action.resultSet.map(r => +r.auditLogId))
          : state.deletedScheduledAircraftTripSkeletons.lastAuditLogId
    }
  })),

  on(actions.LoadScheduledAircraftTripSkeleton, state => ({
    ...state,

    scheduledAircraftTripSkeleton: {
      ...state.scheduledAircraftTripSkeleton,
      loading: true,
      loaded: false
    }
  })),

  on(actions.ScheduledAircraftTripSkeletonLoaded, (state, action) => ({
    ...state,

    scheduledAircraftTripSkeleton: {
      ...state.scheduledAircraftTripSkeleton,
      ...scheduledAircraftTripSkeletonAdapter.addOne(action, state.scheduledAircraftTripSkeleton),
      loaded: true,
      loading: false
    }
  })),

  on(actions.LoadScheduledAircraftTripAudit, (state, action) => ({
    ...state,

    scheduledAircraftTripAudit: {
      ...state.scheduledAircraftTripAudit,
      lastAuditLogId:
        state.scheduledAircraftTripAudit.selectedId === action.scheduledAircraftTripId
          ? state.scheduledAircraftTripAudit.lastAuditLogId
          : null,
      loading: true,
      loaded: false
    }
  })),

  on(actions.ScheduledAircraftTripAuditLoaded, (state, action) => ({
    ...state,

    scheduledAircraftTripAudit: {
      ...state.scheduledAircraftTripAudit,
      ...(!state.scheduledAircraftTripAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.scheduledAircraftTripAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.scheduledAircraftTripAudit)),
      loaded: true,
      loading: false,
      selectedId: action.scheduledAircraftTripId,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  on(actions.LoadScheduledFlightAudit, (state, action) => ({
    ...state,

    scheduledFlightAudit: {
      ...state.scheduledFlightAudit,
      lastAuditLogId:
        state.scheduledFlightAudit.selectedId === action.scheduledFlightId
          ? state.scheduledFlightAudit.lastAuditLogId
          : null,
      loading: true,
      loaded: false
    }
  })),

  on(actions.ScheduledFlightAuditLoaded, (state, action) => ({
    ...state,

    scheduledFlightAudit: {
      ...state.scheduledFlightAudit,
      ...(!state.scheduledFlightAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.scheduledFlightAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.scheduledFlightAudit)),
      loaded: true,
      loading: false,
      selectedId: action.scheduledFlightId,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  on(actions.LoadFlightAudit, (state, action) => ({
    ...state,

    flightAudit: {
      ...state.flightAudit,
      lastAuditLogId:
        state.flightAudit.selectedId === action.flightId ? state.flightAudit.lastAuditLogId : null,
      loading: true,
      loaded: false
    }
  })),

  on(actions.FlightAuditLoaded, (state, action) => ({
    ...state,

    flightAudit: {
      ...state.flightAudit,
      ...(!state.flightAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.flightAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.flightAudit)),
      loaded: true,
      loading: false,
      selectedId: action.flightId,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  on(actions.LoadFlightPassengerAudit, (state, action) => ({
    ...state,

    flightPassengerAudit: {
      ...state.flightPassengerAudit,
      lastAuditLogId:
        state.flightPassengerAudit.selectedId === action.flightPassengerId
          ? state.flightPassengerAudit.lastAuditLogId
          : null,
      loading: true,
      loaded: false
    }
  })),

  on(actions.FlightPassengerAuditLoaded, (state, action) => ({
    ...state,

    flightPassengerAudit: {
      ...state.flightPassengerAudit,
      ...(!state.flightPassengerAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.flightPassengerAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.flightPassengerAudit)),
      loaded: true,
      loading: false,
      selectedId: action.flightPassengerId,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  on(actions.LoadFlightPassengerCateringAudit, (state, action) => ({
    ...state,

    flightPassengerCateringAudit: {
      ...state.flightPassengerCateringAudit,
      lastAuditLogId:
        state.flightPassengerCateringAudit.selectedId === action.flightPassengerCateringId
          ? state.flightPassengerCateringAudit.lastAuditLogId
          : null,
      loading: true,
      loaded: false
    }
  })),
  on(actions.FlightPassengerCateringAuditLoaded, (state, action) => ({
    ...state,

    flightPassengerCateringAudit: {
      ...state.flightPassengerCateringAudit,
      ...(!state.flightPassengerCateringAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.flightPassengerCateringAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.flightPassengerCateringAudit)),
      loaded: true,
      loading: false,
      selectedId: action.flightPassengerCateringId,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  on(actions.LoadFlightPassengerHotelAudit, (state, action) => ({
    ...state,

    flightPassengerHotelAudit: {
      ...state.flightPassengerHotelAudit,
      lastAuditLogId:
        state.flightPassengerHotelAudit.selectedId === action.flightPassengerHotelId
          ? state.flightPassengerHotelAudit.lastAuditLogId
          : null,
      loading: true,
      loaded: false
    }
  })),

  on(actions.FlightPassengerHotelAuditLoaded, (state, action) => ({
    ...state,

    flightPassengerHotelAudit: {
      ...state.flightPassengerHotelAudit,
      ...(!state.flightPassengerHotelAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.flightPassengerHotelAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.flightPassengerHotelAudit)),
      loaded: true,
      loading: false,
      selectedId: action.flightPassengerHotelId,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  on(actions.LoadFlightPassengerTransportationAudit, (state, action) => ({
    ...state,

    flightPassengerTransportationAudit: {
      ...state.flightPassengerTransportationAudit,
      lastAuditLogId:
        state.flightPassengerTransportationAudit.selectedId ===
        action.flightPassengerTransportationId
          ? state.flightPassengerTransportationAudit.lastAuditLogId
          : null,
      loading: true,
      loaded: false
    }
  })),

  on(actions.FlightPassengerTransportationAuditLoaded, (state, action) => ({
    ...state,

    flightPassengerTransportationAudit: {
      ...state.flightPassengerTransportationAudit,
      ...(!state.flightPassengerTransportationAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.flightPassengerTransportationAudit)
        : auditLogAdapter.addMany(
            action.audits.resultSet,
            state.flightPassengerTransportationAudit
          )),
      loaded: true,
      loading: false,
      selectedId: action.flightPassengerTransportationId,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  on(actions.LoadFlightCrewMemberAudit, (state, action) => ({
    ...state,

    flightCrewMemberAudit: {
      ...state.flightCrewMemberAudit,
      lastAuditLogId:
        state.flightCrewMemberAudit.selectedId === action.flightCrewMemberId
          ? state.flightCrewMemberAudit.lastAuditLogId
          : null,
      loading: true,
      loaded: false
    }
  })),

  on(actions.FlightCrewMemberAuditLoaded, (state, action) => ({
    ...state,

    flightCrewMemberAudit: {
      ...state.flightCrewMemberAudit,
      ...(!state.flightCrewMemberAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.flightCrewMemberAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.flightCrewMemberAudit)),
      loaded: true,
      loading: false,
      selectedId: action.flightCrewMemberId,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  on(actions.LoadFlightCrewMemberCateringAudit, (state, action) => ({
    ...state,

    flightCrewMemberCateringAudit: {
      ...state.flightCrewMemberCateringAudit,
      lastAuditLogId:
        state.flightCrewMemberCateringAudit.selectedId === action.flightCrewMemberCateringId
          ? state.flightCrewMemberCateringAudit.lastAuditLogId
          : null,
      loading: true,
      loaded: false
    }
  })),

  on(actions.FlightCrewMemberCateringAuditLoaded, (state, action) => ({
    ...state,

    flightCrewMemberCateringAudit: {
      ...state.flightCrewMemberCateringAudit,
      ...(!state.flightCrewMemberCateringAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.flightCrewMemberCateringAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.flightCrewMemberCateringAudit)),
      loaded: true,
      loading: false,
      selectedId: action.flightCrewMemberCateringId,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  on(actions.LoadFlightCrewMemberHotelAudit, (state, action) => ({
    ...state,

    flightCrewMemberHotelAudit: {
      ...state.flightCrewMemberHotelAudit,
      lastAuditLogId:
        state.flightCrewMemberHotelAudit.selectedId === action.flightCrewMemberHotelId
          ? state.flightCrewMemberHotelAudit.lastAuditLogId
          : null,
      loading: true,
      loaded: false
    }
  })),

  on(actions.FlightCrewMemberHotelAuditLoaded, (state, action) => ({
    ...state,

    flightCrewMemberHotelAudit: {
      ...state.flightCrewMemberHotelAudit,
      ...(!state.flightCrewMemberHotelAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.flightCrewMemberHotelAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.flightCrewMemberHotelAudit)),
      loaded: true,
      loading: false,
      selectedId: action.flightCrewMemberHotelId,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  on(actions.LoadFlightCrewMemberTransportationAudit, (state, action) => ({
    ...state,

    flightCrewMemberTransportationAudit: {
      ...state.flightCrewMemberTransportationAudit,
      lastAuditLogId:
        state.flightCrewMemberTransportationAudit.selectedId ===
        action.flightCrewMemberTransportationId
          ? state.flightCrewMemberTransportationAudit.lastAuditLogId
          : null,
      loading: true,
      loaded: false
    }
  })),

  on(actions.FlightCrewMemberTransportationAuditLoaded, (state, action) => ({
    ...state,

    flightCrewMemberTransportationAudit: {
      ...state.flightCrewMemberTransportationAudit,
      ...(!state.flightCrewMemberTransportationAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.flightCrewMemberTransportationAudit)
        : auditLogAdapter.addMany(
            action.audits.resultSet,
            state.flightCrewMemberTransportationAudit
          )),
      loaded: true,
      loading: false,
      selectedId: action.flightCrewMemberTransportationId,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  on(actions.LoadFlightBusinessPersonAudit, (state, action) => ({
    ...state,

    flightBusinessPersonAudit: {
      ...state.flightBusinessPersonAudit,
      lastAuditLogId:
        state.flightBusinessPersonAudit.selectedId === action.flightBusinessPersonId
          ? state.flightBusinessPersonAudit.lastAuditLogId
          : null,
      loading: true,
      loaded: false
    }
  })),

  on(actions.FlightBusinessPersonAuditLoaded, (state, action) => ({
    ...state,

    flightBusinessPersonAudit: {
      ...state.flightBusinessPersonAudit,
      ...(!state.flightBusinessPersonAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.flightBusinessPersonAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.flightBusinessPersonAudit)),
      loaded: true,
      loading: false,
      selectedId: action.flightBusinessPersonId,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  on(actions.LoadScheduledEventAudit, (state, action) => ({
    ...state,

    scheduledEventAudit: {
      ...state.scheduledEventAudit,
      lastAuditLogId:
        state.scheduledEventAudit.selectedId === action.scheduledEventId
          ? state.scheduledEventAudit.lastAuditLogId
          : null,
      loading: true,
      loaded: false
    }
  })),

  on(actions.ScheduledEventAuditLoaded, (state, action) => ({
    ...state,

    scheduledEventAudit: {
      ...state.scheduledEventAudit,
      ...(!state.scheduledEventAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.scheduledEventAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.scheduledEventAudit)),
      loaded: true,
      loading: false,
      selectedId: action.scheduledEventId,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  on(actions.LoadLegNoteAudit, (state, action) => ({
    ...state,

    legNoteAudit: {
      ...state.legNoteAudit,
      lastAuditLogId:
        state.legNoteAudit.selectedId === action.legNoteId
          ? state.legNoteAudit.lastAuditLogId
          : null,
      loading: true,
      loaded: false
    }
  })),

  on(actions.LegNoteAuditLoaded, (state, action) => ({
    ...state,

    legNoteAudit: {
      ...state.legNoteAudit,
      ...(!state.legNoteAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.legNoteAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.legNoteAudit)),
      loaded: true,
      loading: false,
      selectedId: action.legNoteId,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  on(actions.LoadFlightFuelAudit, (state, action) => ({
    ...state,

    flightFuelAudit: {
      ...state.flightFuelAudit,
      lastAuditLogId:
        state.flightFuelAudit.selectedId === action.flightFuelId
          ? state.flightFuelAudit.lastAuditLogId
          : null,
      loading: true,
      loaded: false
    }
  })),

  on(actions.FlightFuelAuditLoaded, (state, action) => ({
    ...state,

    flightFuelAudit: {
      ...state.flightFuelAudit,
      ...(!state.flightFuelAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.flightFuelAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.flightFuelAudit)),
      loaded: true,
      loading: false,
      selectedId: action.flightFuelId,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  // V2 Reducers for lazy loading

  on(actions.LoadDeletedTrips, state => ({
    ...state,
    deletedTrips: {
      ...state.deletedTrips,
      loading: true,
      loaded: false
    }
  })),

  on(actions.DeletedTripsLoaded, (state, action) => ({
    ...state,
    deletedTrips: {
      ...state.deletedTrips,
      ...(state.deletedTrips.pageNumber === 1
        ? deletedTripsAdapter.setAll(action.resultSet, state.deletedTrips)
        : deletedTripsAdapter.addMany(action.resultSet, state.deletedTrips)),
      loading: false,
      loaded: true,
      pageNumber: action.pageNumber
    }
  })),

  on(actions.LoadTripLogs, (state, action) => ({
    ...state,
    tripLogs: {
      // Очищаем данные если это новый trip
      ...(state.tripLogs.selectedId !== action.tripId
        ? tripLogsAdapter.removeAll(state.tripLogs)
        : state.tripLogs),
      lastId: state.tripLogs.selectedId === action.tripId ? state.tripLogs.lastId : null,
      loading: true,
      loaded: false,
      selectedId: action.tripId,
      pageNumber: 1,
      totalCount: 0
    }
  })),

  on(actions.TripLogsLoaded, (state, action) => ({
    ...state,
    tripLogs: {
      ...state.tripLogs,
      ...(!state.tripLogs.lastId
        ? tripLogsAdapter.setAll(action.logs.resultSet, state.tripLogs)
        : tripLogsAdapter.addMany(action.logs.resultSet, state.tripLogs)),
      loaded: true,
      loading: false,
      selectedId: action.tripId,
      pageNumber: action.logs.pageNumber,
      totalCount: action.logs.totalCount,
      lastId:
        action.logs.resultSet && action.logs.resultSet.length > 0
          ? Math.min(...action.logs.resultSet.map(r => +r.id))
          : state.tripLogs.lastId
    }
  })),

  on(actions.LoadDeletedTripLegs, (state, action) => ({
    ...state,
    deletedTripLegs: {
      // Очищаем данные если это новый trip
      ...(state.deletedTripLegs.selectedId !== action.tripId
        ? deletedTripLegsAdapter.removeAll(state.deletedTripLegs)
        : state.deletedTripLegs),
      loading: true,
      loaded: false,
      selectedId: action.tripId,
      pageNumber: 1,
      totalCount: 0
    }
  })),

  on(actions.DeletedTripLegsLoaded, (state, action) => ({
    ...state,
    deletedTripLegs: {
      ...state.deletedTripLegs,
      ...(state.deletedTripLegs.selectedId !== action.tripId
        ? deletedTripLegsAdapter.setAll(action.legs.resultSet, state.deletedTripLegs)
        : deletedTripLegsAdapter.addMany(action.legs.resultSet, state.deletedTripLegs)),
      loaded: true,
      loading: false,
      selectedId: action.tripId,
      pageNumber: action.legs.pageNumber,
      totalCount: action.legs.totalCount
    }
  })),

  on(actions.DeletedTripLegsLoadError, state => ({
    ...state,
    deletedTripLegs: {
      ...state.deletedTripLegs,
      loading: false
    }
  })),

  // Trip Legs
  on(actions.LoadTripLegs, (state, action) => ({
    ...state,
    tripLegs: {
      ...(state.tripLegs.selectedId !== action.tripId
        ? tripLegsAdapter.removeAll(state.tripLegs)
        : state.tripLegs),
      loading: true,
      loaded: false,
      selectedId: action.tripId,
      pageNumber: 1,
      totalCount: 0
    }
  })),

  on(actions.TripLegsLoaded, (state, action) => ({
    ...state,
    tripLegs: {
      ...state.tripLegs,
      ...(state.tripLegs.selectedId !== action.tripId
        ? tripLegsAdapter.setAll(action.legs.resultSet, state.tripLegs)
        : tripLegsAdapter.addMany(action.legs.resultSet, state.tripLegs)),
      loaded: true,
      loading: false,
      selectedId: action.tripId,
      pageNumber: action.legs.pageNumber,
      totalCount: action.legs.totalCount
    }
  })),

  on(actions.TripLegsLoadError, state => ({
    ...state,
    tripLegs: {
      ...state.tripLegs,
      loading: false
    }
  })),

  // Leg Event Logs (Dep/Arr Time)
  on(actions.LoadLegEventLogs, (state, action) => ({
    ...state,
    legEventLogs: {
      ...(state.legEventLogs.selectedId !== action.eventId
        ? legEventLogsAdapter.removeAll(state.legEventLogs)
        : state.legEventLogs),
      lastId: state.legEventLogs.selectedId === action.eventId ? state.legEventLogs.lastId : null,
      loading: true,
      loaded: false,
      selectedId: action.eventId,
      pageNumber: 1,
      totalCount: 0
    }
  })),

  on(actions.LegEventLogsLoaded, (state, action) => ({
    ...state,
    legEventLogs: {
      ...state.legEventLogs,
      ...(!state.legEventLogs.lastId
        ? legEventLogsAdapter.setAll(action.logs.resultSet, state.legEventLogs)
        : legEventLogsAdapter.addMany(action.logs.resultSet, state.legEventLogs)),
      loaded: true,
      loading: false,
      selectedId: action.eventId,
      pageNumber: action.logs.pageNumber,
      totalCount: action.logs.totalCount,
      lastId:
        action.logs.resultSet && action.logs.resultSet.length > 0
          ? Math.min(...action.logs.resultSet.map(r => +r.id))
          : state.legEventLogs.lastId
    }
  })),

  // Leg Flight Logs (Details)
  on(actions.LoadLegFlightLogs, (state, action) => ({
    ...state,
    legFlightLogs: {
      ...(state.legFlightLogs.selectedId !== action.flightId
        ? legFlightLogsAdapter.removeAll(state.legFlightLogs)
        : state.legFlightLogs),
      lastId: state.legFlightLogs.selectedId === action.flightId ? state.legFlightLogs.lastId : null,
      loading: true,
      loaded: false,
      selectedId: action.flightId,
      pageNumber: 1,
      totalCount: 0
    }
  })),

  on(actions.LegFlightLogsLoaded, (state, action) => ({
    ...state,
    legFlightLogs: {
      ...state.legFlightLogs,
      ...(!state.legFlightLogs.lastId
        ? legFlightLogsAdapter.setAll(action.logs.resultSet, state.legFlightLogs)
        : legFlightLogsAdapter.addMany(action.logs.resultSet, state.legFlightLogs)),
      loaded: true,
      loading: false,
      selectedId: action.flightId,
      pageNumber: action.logs.pageNumber,
      totalCount: action.logs.totalCount,
      lastId:
        action.logs.resultSet && action.logs.resultSet.length > 0
          ? Math.min(...action.logs.resultSet.map(r => +r.id))
          : state.legFlightLogs.lastId
    }
  })),

  // Leg Fuel Logs
  on(actions.LoadLegFuelLogs, (state, action) => ({
    ...state,
    legFuelLogs: {
      ...(state.legFuelLogs.selectedId !== action.fuelId
        ? legFuelLogsAdapter.removeAll(state.legFuelLogs)
        : state.legFuelLogs),
      lastId: state.legFuelLogs.selectedId === action.fuelId ? state.legFuelLogs.lastId : null,
      loading: true,
      loaded: false,
      selectedId: action.fuelId,
      pageNumber: 1,
      totalCount: 0
    }
  })),

  on(actions.LegFuelLogsLoaded, (state, action) => ({
    ...state,
    legFuelLogs: {
      ...state.legFuelLogs,
      ...(!state.legFuelLogs.lastId
        ? legFuelLogsAdapter.setAll(action.logs.resultSet, state.legFuelLogs)
        : legFuelLogsAdapter.addMany(action.logs.resultSet, state.legFuelLogs)),
      loaded: true,
      loading: false,
      selectedId: action.fuelId,
      pageNumber: action.logs.pageNumber,
      totalCount: action.logs.totalCount,
      lastId:
        action.logs.resultSet && action.logs.resultSet.length > 0
          ? Math.min(...action.logs.resultSet.map(r => +r.id))
          : state.legFuelLogs.lastId
    }
  })),

  // Flight Crew Members
  on(actions.LoadFlightCrewMembers, (state, action) => ({
    ...state,
    flightCrewMembers: flightCrewMembersAdapter.removeAll({
      ...state.flightCrewMembers,
      loading: true,
      loaded: false,
      selectedFlightId: action.flightId,
      pageNumber: 1,
      totalCount: 0
    })
  })),

  on(actions.LoadNextPageFlightCrewMembers, state => ({
    ...state,
    flightCrewMembers: {
      ...state.flightCrewMembers,
      loading: true
    }
  })),

  on(actions.FlightCrewMembersLoaded, (state, action) => ({
    ...state,
    flightCrewMembers: flightCrewMembersAdapter.addMany(action.data.resultSet, {
      ...state.flightCrewMembers,
      loaded: true,
      loading: false,
      selectedFlightId: action.flightId,
      pageNumber: action.data.pageNumber,
      totalCount: action.data.totalCount
    })
  })),

  // Crew Logs
  on(actions.LoadCrewLogs, (state, action) => ({
    ...state,
    crewLogs: {
      ...(state.crewLogs.selectedId !== action.crewId
        ? crewLogsAdapter.removeAll(state.crewLogs)
        : state.crewLogs),
      lastId: state.crewLogs.selectedId === action.crewId ? state.crewLogs.lastId : null,
      loading: true,
      loaded: false,
      selectedId: action.crewId,
      pageNumber: 1,
      totalCount: 0
    }
  })),

  on(actions.CrewLogsLoaded, (state, action) => ({
    ...state,
    crewLogs: {
      ...state.crewLogs,
      ...(!state.crewLogs.lastId
        ? crewLogsAdapter.setAll(action.logs.resultSet, state.crewLogs)
        : crewLogsAdapter.addMany(action.logs.resultSet, state.crewLogs)),
      loaded: true,
      loading: false,
      selectedId: action.crewId,
      pageNumber: action.logs.pageNumber,
      totalCount: action.logs.totalCount,
      lastId:
        action.logs.resultSet && action.logs.resultSet.length > 0
          ? Math.min(...action.logs.resultSet.map(r => +r.id))
          : state.crewLogs.lastId
    }
  })),

  // Crew Catering Logs
  on(actions.LoadCrewCateringLogs, (state, action) => ({
    ...state,
    crewCateringLogs: {
      ...(state.crewCateringLogs.selectedId !== action.cateringId
        ? crewCateringLogsAdapter.removeAll(state.crewCateringLogs)
        : state.crewCateringLogs),
      lastId: state.crewCateringLogs.selectedId === action.cateringId ? state.crewCateringLogs.lastId : null,
      loading: true,
      loaded: false,
      selectedId: action.cateringId,
      pageNumber: 1,
      totalCount: 0
    }
  })),

  on(actions.CrewCateringLogsLoaded, (state, action) => ({
    ...state,
    crewCateringLogs: {
      ...state.crewCateringLogs,
      ...(!state.crewCateringLogs.lastId
        ? crewCateringLogsAdapter.setAll(action.logs.resultSet, state.crewCateringLogs)
        : crewCateringLogsAdapter.addMany(action.logs.resultSet, state.crewCateringLogs)),
      loaded: true,
      loading: false,
      selectedId: action.cateringId,
      pageNumber: action.logs.pageNumber,
      totalCount: action.logs.totalCount,
      lastId:
        action.logs.resultSet && action.logs.resultSet.length > 0
          ? Math.min(...action.logs.resultSet.map(r => +r.id))
          : state.crewCateringLogs.lastId
    }
  })),

  // Crew Hotel Logs
  on(actions.LoadCrewHotelLogs, (state, action) => ({
    ...state,
    crewHotelLogs: {
      ...(state.crewHotelLogs.selectedId !== action.hotelId
        ? crewHotelLogsAdapter.removeAll(state.crewHotelLogs)
        : state.crewHotelLogs),
      lastId: state.crewHotelLogs.selectedId === action.hotelId ? state.crewHotelLogs.lastId : null,
      loading: true,
      loaded: false,
      selectedId: action.hotelId,
      pageNumber: 1,
      totalCount: 0
    }
  })),

  on(actions.CrewHotelLogsLoaded, (state, action) => ({
    ...state,
    crewHotelLogs: {
      ...state.crewHotelLogs,
      ...(!state.crewHotelLogs.lastId
        ? crewHotelLogsAdapter.setAll(action.logs.resultSet, state.crewHotelLogs)
        : crewHotelLogsAdapter.addMany(action.logs.resultSet, state.crewHotelLogs)),
      loaded: true,
      loading: false,
      selectedId: action.hotelId,
      pageNumber: action.logs.pageNumber,
      totalCount: action.logs.totalCount,
      lastId:
        action.logs.resultSet && action.logs.resultSet.length > 0
          ? Math.min(...action.logs.resultSet.map(r => +r.id))
          : state.crewHotelLogs.lastId
    }
  })),

  // Crew Transportation Logs
  on(actions.LoadCrewTransportationLogs, (state, action) => ({
    ...state,
    crewTransportationLogs: {
      ...(state.crewTransportationLogs.selectedId !== action.transportationId
        ? crewTransportationLogsAdapter.removeAll(state.crewTransportationLogs)
        : state.crewTransportationLogs),
      lastId: state.crewTransportationLogs.selectedId === action.transportationId ? state.crewTransportationLogs.lastId : null,
      loading: true,
      loaded: false,
      selectedId: action.transportationId,
      pageNumber: 1,
      totalCount: 0
    }
  })),

  on(actions.CrewTransportationLogsLoaded, (state, action) => ({
    ...state,
    crewTransportationLogs: {
      ...state.crewTransportationLogs,
      ...(!state.crewTransportationLogs.lastId
        ? crewTransportationLogsAdapter.setAll(action.logs.resultSet, state.crewTransportationLogs)
        : crewTransportationLogsAdapter.addMany(action.logs.resultSet, state.crewTransportationLogs)),
      loaded: true,
      loading: false,
      selectedId: action.transportationId,
      pageNumber: action.logs.pageNumber,
      totalCount: action.logs.totalCount,
      lastId:
        action.logs.resultSet && action.logs.resultSet.length > 0
          ? Math.min(...action.logs.resultSet.map(r => +r.id))
          : state.crewTransportationLogs.lastId
    }
  })),

  // Flight Passengers
  on(actions.LoadFlightPassengers, (state, action) => ({
    ...state,
    flightPassengers: flightPassengersAdapter.removeAll({
      ...state.flightPassengers,
      loading: true,
      loaded: false,
      selectedFlightId: action.flightId,
      pageNumber: 1,
      totalCount: 0
    })
  })),

  on(actions.LoadNextPageFlightPassengers, state => ({
    ...state,
    flightPassengers: {
      ...state.flightPassengers,
      loading: true
    }
  })),

  on(actions.FlightPassengersLoaded, (state, action) => ({
    ...state,
    flightPassengers: flightPassengersAdapter.addMany(action.data.resultSet, {
      ...state.flightPassengers,
      loaded: true,
      loading: false,
      selectedFlightId: action.flightId,
      pageNumber: action.data.pageNumber,
      totalCount: action.data.totalCount
    })
  })),

  // Passenger Logs
  on(actions.LoadPassengerLogs, (state, action) => ({
    ...state,
    passengerLogs: {
      ...(state.passengerLogs.selectedId !== action.paxId
        ? passengerLogsAdapter.removeAll(state.passengerLogs)
        : state.passengerLogs),
      lastId: state.passengerLogs.selectedId === action.paxId ? state.passengerLogs.lastId : null,
      loading: true,
      loaded: false,
      selectedId: action.paxId,
      pageNumber: 1,
      totalCount: 0
    }
  })),

  on(actions.PassengerLogsLoaded, (state, action) => ({
    ...state,
    passengerLogs: {
      ...state.passengerLogs,
      ...(!state.passengerLogs.lastId
        ? passengerLogsAdapter.setAll(action.logs.resultSet, state.passengerLogs)
        : passengerLogsAdapter.addMany(action.logs.resultSet, state.passengerLogs)),
      loaded: true,
      loading: false,
      selectedId: action.paxId,
      pageNumber: action.logs.pageNumber,
      totalCount: action.logs.totalCount,
      lastId:
        action.logs.resultSet && action.logs.resultSet.length > 0
          ? Math.min(...action.logs.resultSet.map(r => +r.id))
          : state.passengerLogs.lastId
    }
  })),

  // Passenger Catering Logs
  on(actions.LoadPassengerCateringLogs, (state, action) => ({
    ...state,
    passengerCateringLogs: {
      ...(state.passengerCateringLogs.selectedId !== action.cateringId
        ? passengerCateringLogsAdapter.removeAll(state.passengerCateringLogs)
        : state.passengerCateringLogs),
      lastId: state.passengerCateringLogs.selectedId === action.cateringId ? state.passengerCateringLogs.lastId : null,
      loading: true,
      loaded: false,
      selectedId: action.cateringId,
      pageNumber: 1,
      totalCount: 0
    }
  })),

  on(actions.PassengerCateringLogsLoaded, (state, action) => ({
    ...state,
    passengerCateringLogs: {
      ...state.passengerCateringLogs,
      ...(!state.passengerCateringLogs.lastId
        ? passengerCateringLogsAdapter.setAll(action.logs.resultSet, state.passengerCateringLogs)
        : passengerCateringLogsAdapter.addMany(action.logs.resultSet, state.passengerCateringLogs)),
      loaded: true,
      loading: false,
      selectedId: action.cateringId,
      pageNumber: action.logs.pageNumber,
      totalCount: action.logs.totalCount,
      lastId:
        action.logs.resultSet && action.logs.resultSet.length > 0
          ? Math.min(...action.logs.resultSet.map(r => +r.id))
          : state.passengerCateringLogs.lastId
    }
  })),

  // Passenger Hotel Logs
  on(actions.LoadPassengerHotelLogs, (state, action) => ({
    ...state,
    passengerHotelLogs: {
      ...(state.passengerHotelLogs.selectedId !== action.hotelId
        ? passengerHotelLogsAdapter.removeAll(state.passengerHotelLogs)
        : state.passengerHotelLogs),
      lastId: state.passengerHotelLogs.selectedId === action.hotelId ? state.passengerHotelLogs.lastId : null,
      loading: true,
      loaded: false,
      selectedId: action.hotelId,
      pageNumber: 1,
      totalCount: 0
    }
  })),

  on(actions.PassengerHotelLogsLoaded, (state, action) => ({
    ...state,
    passengerHotelLogs: {
      ...state.passengerHotelLogs,
      ...(!state.passengerHotelLogs.lastId
        ? passengerHotelLogsAdapter.setAll(action.logs.resultSet, state.passengerHotelLogs)
        : passengerHotelLogsAdapter.addMany(action.logs.resultSet, state.passengerHotelLogs)),
      loaded: true,
      loading: false,
      selectedId: action.hotelId,
      pageNumber: action.logs.pageNumber,
      totalCount: action.logs.totalCount,
      lastId:
        action.logs.resultSet && action.logs.resultSet.length > 0
          ? Math.min(...action.logs.resultSet.map(r => +r.id))
          : state.passengerHotelLogs.lastId
    }
  })),

  // Passenger Transportation Logs
  on(actions.LoadPassengerTransportationLogs, (state, action) => ({
    ...state,
    passengerTransportationLogs: {
      ...(state.passengerTransportationLogs.selectedId !== action.transportationId
        ? passengerTransportationLogsAdapter.removeAll(state.passengerTransportationLogs)
        : state.passengerTransportationLogs),
      lastId: state.passengerTransportationLogs.selectedId === action.transportationId ? state.passengerTransportationLogs.lastId : null,
      loading: true,
      loaded: false,
      selectedId: action.transportationId,
      pageNumber: 1,
      totalCount: 0
    }
  })),

  on(actions.PassengerTransportationLogsLoaded, (state, action) => ({
    ...state,
    passengerTransportationLogs: {
      ...state.passengerTransportationLogs,
      ...(!state.passengerTransportationLogs.lastId
        ? passengerTransportationLogsAdapter.setAll(action.logs.resultSet, state.passengerTransportationLogs)
        : passengerTransportationLogsAdapter.addMany(action.logs.resultSet, state.passengerTransportationLogs)),
      loaded: true,
      loading: false,
      selectedId: action.transportationId,
      pageNumber: action.logs.pageNumber,
      totalCount: action.logs.totalCount,
      lastId:
        action.logs.resultSet && action.logs.resultSet.length > 0
          ? Math.min(...action.logs.resultSet.map(r => +r.id))
          : state.passengerTransportationLogs.lastId
    }
  })),

  // Tier-1: Flight Schedulers
  on(actions.LoadFlightSchedulers, actions.LoadScheduledFlightSchedulers, (state, action) => ({
    ...state,
    flightSchedulers: flightSchedulersAdapter.removeAll({
      ...state.flightSchedulers,
      loading: true,
      loaded: false,
      selectedFlightId: action.flightId,
      pageNumber: 1,
      totalCount: 0
    })
  })),

  on(actions.LoadNextPageFlightSchedulers, actions.LoadNextPageScheduledFlightSchedulers, (state) => ({
    ...state,
    flightSchedulers: {
      ...state.flightSchedulers,
      loading: true
    }
  })),

  on(actions.FlightSchedulersLoaded, (state, action) => ({
    ...state,
    flightSchedulers: flightSchedulersAdapter.addMany(action.data.resultSet, {
      ...state.flightSchedulers,
      loaded: true,
      loading: false,
      selectedFlightId: action.flightId,
      pageNumber: action.data.pageNumber,
      totalCount: action.data.totalCount
    })
  })),

  on(actions.FlightSchedulersLoadError, (state) => ({
    ...state,
    flightSchedulers: {
      ...state.flightSchedulers,
      loading: false,
      loaded: true
    }
  })),

  // Tier-2: Scheduler Logs
  on(actions.LoadSchedulerLogs, (state, action) => ({
    ...state,
    schedulerLogs: {
      ...(state.schedulerLogs.selectedId !== action.schedulerId
        ? schedulerLogsAdapter.removeAll(state.schedulerLogs)
        : state.schedulerLogs),
      lastId: state.schedulerLogs.selectedId === action.schedulerId ? state.schedulerLogs.lastId : null,
      loading: true,
      loaded: false,
      selectedId: action.schedulerId,
      pageNumber: 1,
      totalCount: 0
    }
  })),

  on(actions.SchedulerLogsLoaded, (state, action) => ({
    ...state,
    schedulerLogs: {
      ...state.schedulerLogs,
      ...(!state.schedulerLogs.lastId
        ? schedulerLogsAdapter.setAll(action.logs.resultSet, state.schedulerLogs)
        : schedulerLogsAdapter.addMany(action.logs.resultSet, state.schedulerLogs)),
      loaded: true,
      loading: false,
      selectedId: action.schedulerId,
      pageNumber: action.logs.pageNumber,
      totalCount: action.logs.totalCount,
      lastId:
        action.logs.resultSet && action.logs.resultSet.length > 0
          ? Math.min(...action.logs.resultSet.map(r => +r.id))
          : state.schedulerLogs.lastId
    }
  })),

  on(actions.SchedulerLogsLoadError, (state) => ({
    ...state,
    schedulerLogs: {
      ...state.schedulerLogs,
      loading: false,
      loaded: true
    }
  }))
);

export function reducer(state: State | undefined, action: Action) {
  return tripReducer(state, action);
}
